// axiosInstance.js
import axios from 'axios'
import decodeJwt from 'jwt-decode'
import { BASE_URL } from '../../utils/constants/url'
import { setLocalStorageValue } from '../../utils/helpers/localStorageHelperMethods'
import localStorageIds from '../../utils/constants/localStorageIds'
import generalHeader from '../../utils/constants/generalHeader'
import errorMessageMapper from '../../utils/helpers/errorMessageMapper'

let isRefreshing = false // Flag to check if a token refresh is in progress
let failedQueue = [] // Queue to hold requests that fail due to a 401
let tokenRefreshPromise // To hold the promise of the refresh token request
let errorNotificationCallback = null // Callback function to show error notifications

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: BASE_URL, // Set your API base URL
})

// Function to process the queue
const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })

  // Clear the queue
  failedQueue = []
}

// Function to refresh the token
async function refreshToken() {
  try {
    const response = await axios.get(`${BASE_URL}/auth/refresh-token`, {
      withCredentials: true,
    })

    // Update the token in localStorage
    const newAccessToken = response?.data?.accessToken
    setLocalStorageValue([{ id: localStorageIds.TOKEN, value: newAccessToken }])

    // Return the new token
    return newAccessToken
  } catch (error) {
    console.error('Failed to refresh token')
    throw error // Re-throw to handle in interceptor
  }
}

// Shared refresh with deduplication — use this everywhere instead of calling the endpoint directly.
// Ensures concurrent callers (interceptor + authProvider) share a single in-flight request,
// preventing token rotation from invalidating a valid refresh token mid-flight.
export async function sharedRefreshToken() {
  if (!isRefreshing) {
    isRefreshing = true
    tokenRefreshPromise = refreshToken()
      .then((newAccessToken) => {
        axiosInstance.defaults.headers = generalHeader(newAccessToken)
        processQueue(null, newAccessToken)
        return newAccessToken
      })
      .catch((err) => {
        processQueue(err, null)
        throw err
      })
      .finally(() => {
        isRefreshing = false
        tokenRefreshPromise = null
      })
  }
  return tokenRefreshPromise
}

// The stored access token, refreshed first when it expires within 30s. For SignalR, which sends the
// token once per (re)connect and can't retry a 401 the way the interceptor does. If the refresh
// fails, the stored token goes out and the hub decides.
export async function getFreshAccessToken() {
  const token = localStorage.getItem(localStorageIds.TOKEN)
  try {
    if (token && decodeJwt(token).exp * 1000 > Date.now() + 30000) return token
    return await sharedRefreshToken()
  } catch {
    return token
  }
}

// Add a request interceptor to validate the token
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(localStorageIds.TOKEN)

    // If no token is available, reject the request
    if (!token) {
      return Promise.reject(new axios.CanceledError('No auth token'))
    }

    // Add the token to the headers
    config.headers = generalHeader(token)

    // If the URL starts with "v1", remove "/api" from the baseURL
    if (config.url?.startsWith('v1')) {
      config.baseURL = (BASE_URL || '').replace(/\/api$/, '')
    }

    return config
  },
  (error) => Promise.reject(error),
)

// Add a response interceptor to handle 401 errors
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config

    // If error status is 401 and the request is not for the refresh token
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true // Mark the request as retried

      // Wait for the token refresh to complete (deduplicates concurrent calls)
      let newAccessToken
      try {
        newAccessToken = await sharedRefreshToken()
      } catch (err) {
        // skipLoginRedirect: the caller treats an expired session as logged out itself (pages
        // reachable without one, where a reload to a bare /login would lose what they were showing).
        if (!originalRequest.skipLoginRedirect) window.location.href = '/login'
        throw err
      }
      originalRequest.headers = generalHeader(newAccessToken) // Update the original request's headers

      // Retry the original request with the new token
      return axiosInstance(originalRequest)
    }

    // Handle other errors with notification callback
    // Skip notification if the request has skipErrorNotification flag
    const skipNotification = originalRequest?.skipErrorNotification || axios.isCancel(error)

    if (!skipNotification && errorNotificationCallback && error.response) {
      const { status, data } = error.response

      // Use server message if available, otherwise use the mapper
      const message = data?.details || data?.message || data?.error || errorMessageMapper(status)

      // Call the registered callback with error details
      errorNotificationCallback({
        message,
        statusCode: status,
        error: error.response,
      })
    } else if (!skipNotification && errorNotificationCallback && !error.response) {
      // Network error or request was cancelled
      errorNotificationCallback({
        message: errorMessageMapper(null),
        statusCode: null,
        error,
      })
    }

    return Promise.reject(error)
  },
)

// Function to register error notification callback
export const registerErrorNotification = (callback) => {
  errorNotificationCallback = callback
}

export default axiosInstance
